import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import './ActivityVideos.css';
import video1 from '../images/AidForHandicapped.mp4';
import video2 from '../images/EmploymentAid.mp4';
import video3 from '../images/FreeEye.mp4';
import video4 from '../images/IRCCharitable.mp4';
import video5 from '../images/IRCDastarkhwan.mp4';
import video6 from '../images/IRCFreeWaterSupply.mp4';
import video7 from '../images/IRCTechincalInstitute.mp4';
import video8 from '../images/IRCTechnicalInstitute.mp4';
import video9 from '../images/IRCWomenEmpowerment.mp4';
import video10 from '../images/IRCWomenEmpowermen.mp4';

const ActivityVideos = () => {
    const [playingStates, setPlayingStates] = useState({});
    const [progressStates, setProgressStates] = useState({});
    const [loadingStates, setLoadingStates] = useState({});
    const [errorStates, setErrorStates] = useState({});
    const [selectedVideo, setSelectedVideo] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const videoRefs = useRef({});
    const modalVideoRef = useRef(null);
    const progressIntervals = useRef({});

    const videos = useMemo(() => [
        {
            id: 1,
            url: video1,
            charityName: "Aid For Handicapped",
            description: "Aid For Handicapped.",
        },
        {
            id: 2,
            url: video2,
            charityName: "Employment Aid",
            description: "Employment Aid.",
        },
        {
            id: 3,
            url: video3,
            charityName: "Free Eye",
            description: "Free Eye.",
        },
        {
            id: 4,
            url: video4,
            charityName: "IRC Charitable",
            description: "IRC Charitable.",
        },
        {
            id: 5,
            url: video5,
            charityName: "IRC Dastarkhwan",
            description: "IRC Dastarkhwan.",
        },
        {
            id: 6,
            url: video6,
            charityName: "IRC Free Water Supply",
            description: "IRC Free Water Supply.",
        },
        {
            id: 7,
            url: video7,
            charityName: "IRC Techincal Institute",
            description: "IRC Techincal Institute.",
        },
        {
            id: 8,
            url: video8,
            charityName: "IRC Women Empowerment",
            description: "IRC Women Empowerment.",
        },
        // {
        //     id: 9,
        //     url: video8,
        //     charityName: "IRC Women Empowerment Center",
        //     description: "IRC Women Empowerment Center.",
        // },
        // {
        //     id: 10,
        //     url: video8,
        //     charityName: "Earth Guardians",
        //     description: "Protecting endangered ecosystems and wildlife through conservation efforts worldwide.",
        // }
    ], []);

    const stopProgressBar = useCallback((videoId) => {
        if (progressIntervals.current[videoId]) {
            clearInterval(progressIntervals.current[videoId]);
        }
    }, []);

    const restartVideo = useCallback((videoId) => {
        const video = videoRefs.current[videoId];
        if (video) {
            video.currentTime = 0;
            video.play().catch(error => {
                console.error('Error restarting video:', error);
                setErrorStates(prev => ({ ...prev, [videoId]: true }));
            });
        }
    }, []);

    const startProgressBar = useCallback((videoId) => {
        stopProgressBar(videoId);
        setProgressStates(prev => ({ ...prev, [videoId]: 0 }));

        progressIntervals.current[videoId] = setInterval(() => {
            setProgressStates(prev => {
                const currentProgress = prev[videoId] || 0;
                if (currentProgress >= 100) {
                    stopProgressBar(videoId);
                    restartVideo(videoId);
                    return { ...prev, [videoId]: 0 };
                }
                return { ...prev, [videoId]: currentProgress + (100 / (15 * 10)) };
            });
        }, 100);
    }, [stopProgressBar, restartVideo]);

    const handlePlayPause = useCallback((videoId, e) => {
        e?.stopPropagation();
        const video = videoRefs.current[videoId];
        if (video) {
            if (playingStates[videoId]) {
                video.pause();
                stopProgressBar(videoId);
            } else {
                video.play().catch(error => {
                    console.error('Error playing video:', error);
                    setErrorStates(prev => ({ ...prev, [videoId]: true }));
                });
                startProgressBar(videoId);
            }
            setPlayingStates(prev => ({
                ...prev,
                [videoId]: !prev[videoId]
            }));
        }
    }, [playingStates, stopProgressBar, startProgressBar]);

    const handleVideoClick = useCallback((videoId) => {
        const video = videos.find(v => v.id === videoId);
        if (video) {
            setSelectedVideo(video);
            setIsModalOpen(true);
            // Pause any playing video in grid
            Object.keys(playingStates).forEach(id => {
                if (playingStates[id] && videoRefs.current[id]) {
                    videoRefs.current[id].pause();
                    stopProgressBar(parseInt(id));
                }
            });
        }
    }, [videos, playingStates, stopProgressBar]);

    const handleModalVideoClick = useCallback(() => {
        if (modalVideoRef.current) {
            if (modalVideoRef.current.paused) {
                modalVideoRef.current.play();
            } else {
                modalVideoRef.current.pause();
            }
        }
    }, []);

    const playModalVideo = useCallback(() => {
        if (modalVideoRef.current && selectedVideo) {
            modalVideoRef.current.src = selectedVideo.url;
            modalVideoRef.current.load();
            modalVideoRef.current.play().catch(error => {
                console.error('Error playing modal video:', error);
            });
        }
    }, [selectedVideo]);

    const handleNextVideo = useCallback(() => {
        if (selectedVideo) {
            const currentIndex = videos.findIndex(v => v.id === selectedVideo.id);
            const nextIndex = (currentIndex + 1) % videos.length;
            setSelectedVideo(videos[nextIndex]);
        }
    }, [selectedVideo, videos]);

    const handlePrevVideo = useCallback(() => {
        if (selectedVideo) {
            const currentIndex = videos.findIndex(v => v.id === selectedVideo.id);
            const prevIndex = (currentIndex - 1 + videos.length) % videos.length;
            setSelectedVideo(videos[prevIndex]);
        }
    }, [selectedVideo, videos]);

    const handleCloseModal = useCallback(() => {
        if (modalVideoRef.current) {
            modalVideoRef.current.pause();
        }
        setIsModalOpen(false);
        setSelectedVideo(null);
    }, []);

    const handleVideoEnd = useCallback((videoId) => {
        restartVideo(videoId);
    }, [restartVideo]);

    const handleVideoLoadStart = useCallback((videoId) => {
        setLoadingStates(prev => ({ ...prev, [videoId]: true }));
        setErrorStates(prev => ({ ...prev, [videoId]: false }));
    }, []);

    const handleVideoLoadedData = useCallback((videoId) => {
        setLoadingStates(prev => ({ ...prev, [videoId]: false }));
        if (videoId === videos[0].id && !playingStates[videoId]) {
            const video = videoRefs.current[videoId];
            if (video) {
                video.play().catch(error => {
                    console.error('Error auto-playing video:', error);
                });
                startProgressBar(videoId);
                setPlayingStates(prev => ({ ...prev, [videoId]: true }));
            }
        }
    }, [videos, playingStates, startProgressBar]);

    const handleVideoError = useCallback((videoId) => {
        setLoadingStates(prev => ({ ...prev, [videoId]: false }));
        setErrorStates(prev => ({ ...prev, [videoId]: true }));
    }, []);

    const retryVideo = useCallback((videoId, e) => {
        e?.stopPropagation();
        setErrorStates(prev => ({ ...prev, [videoId]: false }));
        setLoadingStates(prev => ({ ...prev, [videoId]: true }));

        const video = videoRefs.current[videoId];
        if (video) {
            video.load();
        }
    }, []);

    useEffect(() => {
        const currentVideoRefs = videoRefs.current;
        const currentProgressIntervals = progressIntervals.current;

        videos.forEach(video => {
            const videoElement = currentVideoRefs[video.id];
            if (videoElement) {
                videoElement.addEventListener('ended', () => handleVideoEnd(video.id));
                videoElement.addEventListener('loadstart', () => handleVideoLoadStart(video.id));
                videoElement.addEventListener('loadeddata', () => handleVideoLoadedData(video.id));
                videoElement.addEventListener('error', () => handleVideoError(video.id));
            }
        });

        return () => {
            videos.forEach(video => {
                const videoElement = currentVideoRefs[video.id];
                if (videoElement) {
                    videoElement.removeEventListener('ended', () => handleVideoEnd(video.id));
                    videoElement.removeEventListener('loadstart', () => handleVideoLoadStart(video.id));
                    videoElement.removeEventListener('loadeddata', () => handleVideoLoadedData(video.id));
                    videoElement.removeEventListener('error', () => handleVideoError(video.id));
                }
                if (currentProgressIntervals[video.id]) {
                    clearInterval(currentProgressIntervals[video.id]);
                }
            });
        };
    }, [videos, handleVideoEnd, handleVideoLoadStart, handleVideoLoadedData, handleVideoError]);

    useEffect(() => {
        if (selectedVideo && isModalOpen) {
            playModalVideo();
        }
    }, [selectedVideo, isModalOpen, playModalVideo]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (isModalOpen) {
                if (e.key === 'Escape') {
                    handleCloseModal();
                } else if (e.key === 'ArrowRight') {
                    handleNextVideo();
                } else if (e.key === 'ArrowLeft') {
                    handlePrevVideo();
                } else if (e.key === ' ') {
                    e.preventDefault();
                    handleModalVideoClick();
                }
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isModalOpen, handleCloseModal, handleNextVideo, handlePrevVideo, handleModalVideoClick]);

    return (
        <>
            <div className="short-videos-container">
                <div className="short-videos-grid">
                    <div className="grid-header">
                        <h1 className="grid-title">Make a Difference</h1>
                        <p className="grid-subtitle">Support these amazing causes and help change lives around the world</p>
                    </div>

                    {videos.map((video) => (
                        <div
                            key={video.id}
                            className="video-card"
                            onClick={() => handleVideoClick(video.id)}
                        >
                            <div className="video-progress">
                                <div
                                    className="progress-bar"
                                    style={{ width: `${progressStates[video.id] || 0}%` }}
                                ></div>
                            </div>

                            <div className="video-header">
                                <h3 className="video-title">{video.charityName}</h3>
                                <div className="video-controls">
                                    <button
                                        className="control-btn"
                                        onClick={(e) => handlePlayPause(video.id, e)}
                                    >
                                        {playingStates[video.id] ? 'Pause' : 'Play'}
                                    </button>
                                </div>
                            </div>

                            <div className="video-wrapper">
                                {loadingStates[video.id] && (
                                    <div className="video-loading">
                                        <div className="loading-spinner"></div>
                                        Loading...
                                    </div>
                                )}

                                {errorStates[video.id] && (
                                    <div className="video-error">
                                        <p>Failed to load video</p>
                                        <button
                                            className="retry-btn"
                                            onClick={(e) => retryVideo(video.id, e)}
                                        >
                                            Retry
                                        </button>
                                    </div>
                                )}

                                {!errorStates[video.id] && (
                                    <video
                                        ref={el => videoRefs.current[video.id] = el}
                                        className="video-player"
                                        muted
                                        playsInline
                                        preload="metadata"
                                        loop
                                    >
                                        <source src={video.url} type="video/mp4" />
                                        Your browser does not support the video tag.
                                    </video>
                                )}

                                <div className="play-pause-overlay">
                                    <div className="play-pause-icon">
                                        {playingStates[video.id] ? '❚❚' : '▶'}
                                    </div>
                                </div>
                            </div>

                            <div className="video-footer">
                                <div className="charity-info">
                                    <h3 className="charity-name">{video.charityName}</h3>
                                    <p className="charity-description">{video.description}</p>
                                </div>

                                <a href="https://pmny.in/HJ2AWtxjLqtp" target="_blank" rel="noopener noreferrer">
                                    <button className="donation-cta">
                                        Donate
                                    </button>
                                </a>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Modal for fullscreen video playback */}
            {isModalOpen && selectedVideo && (
                <div className="video-modal-overlay" onClick={handleCloseModal}>
                    <div className="video-modal-content" onClick={(e) => e.stopPropagation()}>
                        <button className="modal-close-btn" onClick={handleCloseModal}>
                            ×
                        </button>

                        <div className="modal-video-container">
                            <video
                                ref={modalVideoRef}
                                className="modal-video-player"
                                controls
                                autoPlay
                                playsInline
                                onClick={handleModalVideoClick}
                            >
                                <source src={selectedVideo.url} type="video/mp4" />
                                Your browser does not support the video tag.
                            </video>

                            <button className="modal-nav-btn prev-btn" onClick={handlePrevVideo}>
                                ‹
                            </button>
                            <button className="modal-nav-btn next-btn" onClick={handleNextVideo}>
                                ›
                            </button>
                        </div>

                        <div className="modal-video-info">
                            <h2 className="modal-charity-name">{selectedVideo.charityName}</h2>
                            <p className="modal-charity-description">{selectedVideo.description}</p>
                            <a href="https://pmny.in/HJ2AWtxjLqtp" target="_blank" rel="noopener noreferrer">
                                <button className="modal-donation-cta">
                                    Donate Now
                                </button>
                            </a>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ActivityVideos;























// import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
// import './ActivityVideos.css';
// import video1 from '../images/AidForHandicapped.mp4';
// import video2 from '../images/EmploymentAid.mp4';
// import video3 from '../images/FreeEye.mp4';
// import video4 from '../images/IRCCharitable.mp4';
// import video5 from '../images/IRCDastarkhwan.mp4';
// import video6 from '../images/IRCFreeWaterSupply.mp4';
// import video7 from '../images/IRCTechincalInstitute.mp4';
// import video8 from '../images/IRCTechnicalInstitute.mp4';
// import video9 from '../images/IRCWomenEmpowerment.mp4';
// import video10 from '../images/IRCWomenEmpowermen.mp4';


// const ActivityVideos = () => {
//     const [playingStates, setPlayingStates] = useState({});
//     const [progressStates, setProgressStates] = useState({});
//     const [loadingStates, setLoadingStates] = useState({});
//     const [errorStates, setErrorStates] = useState({});
//     const videoRefs = useRef({});
//     const progressIntervals = useRef({});

//     // Real charity and donation themed videos from Pexels
//     const videos = useMemo(() => [
//         {
//             id: 1,
//             url: video1,
//             // title: "Food Drive Success",
//             charityName: "Aid For Handicapped",
//             description: "Aid For Handicapped.",
//             // views: "2.1M",
//             // badge: "urgent",
//             // impact: "125K meals served"
//         },
//         {
//             id: 2,
//             url: video2,
//             // title: "Education for Children",
//             charityName: "Employment Aid",
//             description: "Employment Aid.",
//             // views: "1.5M",
//             // badge: "verified",
//             // impact: "5 schools built"
//         },
//         {
//             id: 3,
//             url: video3,

//             // title: "Medical Aid Mission",
//             charityName: "Free Eye",
//             description: "Free Eye.",
//             // views: "3.4M",
//             // badge: "urgent",
//             // impact: "50K people helped"
//         },
//         {
//             id: 4,
//             url: video4,

//             // title: "Clean Water Project",
//             charityName: "IRC Charitable",
//             description: "IRC Charitable.",
//             // views: "1.8M",
//             // badge: "verified",
//             // impact: "25 villages served"
//         },
//         {
//             id: 5,
//             url: video5,

//             // title: "Disaster Relief",
//             charityName: "IRC Dastarkhwan",
//             description: "IRC Dastarkhwan.",
//             // views: "4.2M",
//             // badge: "urgent",
//             // impact: "15K families assisted"
//         },
//         {
//             id: 6,
//             url: video6,

//             // title: "Animal Shelter Support",
//             charityName: "IRC Free Water Supply",
//             description: "IRC Free Water Supply.",
//             // views: "2.8M",
//             // badge: "verified",
//             // impact: "2K animals saved"
//         },
//         {
//             id: 7,
//             url: video7,

//             // title: "Refugee Assistance",
//             charityName: "IRC Techincal Institute",
//             description: "IRC Techincal Institute.",
//             // views: "3.1M",
//             // badge: "urgent",
//             // impact: "8K refugees supported"
//         },
//         {
//             id: 8,
//             url: video8,

//             // title: "Environmental Conservation",
//             charityName: "IRC Women Empowerment",
//             description: "IRC Women Empowerment.",
//             // donations: "22.4K",
//             // views: "1.9M",
//             // badge: "verified",
//             // impact: "100K acres protected"
//         },
//         {
//             id: 9,
//             url: video8,

//             // title: "Environmental Conservation",
//             charityName: "IRC Women Empowerment Center",
//             description: "IRC Women Empowerment Center.",
//             // donations: "22.4K",
//             // views: "1.9M",
//             // badge: "verified",
//             // impact: "100K acres protected"
//         },
//          {
//             id: 10,
//             url: video8,

//             // title: "Environmental Conservation",
//             charityName: "Earth Guardians",
//             description: "Protecting endangered ecosystems and wildlife through conservation efforts worldwide.",
//             // donations: "22.4K",
//             // views: "1.9M",
//             // badge: "verified",
//             // impact: "100K acres protected"
//         }
//     ], []);

//     const stopProgressBar = useCallback((videoId) => {
//         if (progressIntervals.current[videoId]) {
//             clearInterval(progressIntervals.current[videoId]);
//         }
//     }, []);

//     const restartVideo = useCallback((videoId) => {
//         const video = videoRefs.current[videoId];
//         if (video) {
//             video.currentTime = 0;
//             video.play().catch(error => {
//                 console.error('Error restarting video:', error);
//                 setErrorStates(prev => ({ ...prev, [videoId]: true }));
//             });
//         }
//     }, []);

//     const startProgressBar = useCallback((videoId) => {
//         stopProgressBar(videoId);
//         setProgressStates(prev => ({ ...prev, [videoId]: 0 }));

//         progressIntervals.current[videoId] = setInterval(() => {
//             setProgressStates(prev => {
//                 const currentProgress = prev[videoId] || 0;
//                 if (currentProgress >= 100) {
//                     stopProgressBar(videoId);
//                     restartVideo(videoId);
//                     return { ...prev, [videoId]: 0 };
//                 }
//                 return { ...prev, [videoId]: currentProgress + (100 / (15 * 10)) };
//             });
//         }, 100);
//     }, [stopProgressBar, restartVideo]);

//     const handlePlayPause = useCallback((videoId, e) => {
//         e?.stopPropagation();
//         const video = videoRefs.current[videoId];
//         if (video) {
//             if (playingStates[videoId]) {
//                 video.pause();
//                 stopProgressBar(videoId);
//             } else {
//                 video.play().catch(error => {
//                     console.error('Error playing video:', error);
//                     setErrorStates(prev => ({ ...prev, [videoId]: true }));
//                 });
//                 startProgressBar(videoId);
//             }
//             setPlayingStates(prev => ({
//                 ...prev,
//                 [videoId]: !prev[videoId]
//             }));
//         }
//     }, [playingStates, stopProgressBar, startProgressBar]);

//     const handleVideoClick = useCallback((videoId) => {
//         handlePlayPause(videoId);
//     }, [handlePlayPause]);

//     const handleVideoEnd = useCallback((videoId) => {
//         restartVideo(videoId);
//     }, [restartVideo]);

//     const handleVideoLoadStart = useCallback((videoId) => {
//         setLoadingStates(prev => ({ ...prev, [videoId]: true }));
//         setErrorStates(prev => ({ ...prev, [videoId]: false }));
//     }, []);

//     const handleVideoLoadedData = useCallback((videoId) => {
//         setLoadingStates(prev => ({ ...prev, [videoId]: false }));
//         // Auto-play first video only
//         if (videoId === videos[0].id && !playingStates[videoId]) {
//             const video = videoRefs.current[videoId];
//             if (video) {
//                 video.play().catch(error => {
//                     console.error('Error auto-playing video:', error);
//                 });
//                 startProgressBar(videoId);
//                 setPlayingStates(prev => ({ ...prev, [videoId]: true }));
//             }
//         }
//     }, [videos, playingStates, startProgressBar]);

//     const handleVideoError = useCallback((videoId) => {
//         setLoadingStates(prev => ({ ...prev, [videoId]: false }));
//         setErrorStates(prev => ({ ...prev, [videoId]: true }));
//     }, []);

//     const retryVideo = useCallback((videoId, e) => {
//         e?.stopPropagation();
//         setErrorStates(prev => ({ ...prev, [videoId]: false }));
//         setLoadingStates(prev => ({ ...prev, [videoId]: true }));

//         const video = videoRefs.current[videoId];
//         if (video) {
//             video.load();
//         }
//     }, []);

//     useEffect(() => {
//         // Store current refs in variables for cleanup
//         const currentVideoRefs = videoRefs.current;
//         const currentProgressIntervals = progressIntervals.current;

//         // Initialize all videos
//         videos.forEach(video => {
//             const videoElement = currentVideoRefs[video.id];
//             if (videoElement) {
//                 videoElement.addEventListener('ended', () => handleVideoEnd(video.id));
//                 videoElement.addEventListener('loadstart', () => handleVideoLoadStart(video.id));
//                 videoElement.addEventListener('loadeddata', () => handleVideoLoadedData(video.id));
//                 videoElement.addEventListener('error', () => handleVideoError(video.id));
//             }
//         });

//         return () => {
//             // Cleanup using the stored refs
//             videos.forEach(video => {
//                 const videoElement = currentVideoRefs[video.id];
//                 if (videoElement) {
//                     videoElement.removeEventListener('ended', () => handleVideoEnd(video.id));
//                     videoElement.removeEventListener('loadstart', () => handleVideoLoadStart(video.id));
//                     videoElement.removeEventListener('loadeddata', () => handleVideoLoadedData(video.id));
//                     videoElement.removeEventListener('error', () => handleVideoError(video.id));
//                 }
//                 if (currentProgressIntervals[video.id]) {
//                     clearInterval(currentProgressIntervals[video.id]);
//                 }
//             });
//         };
//     }, [videos, handleVideoEnd, handleVideoLoadStart, handleVideoLoadedData, handleVideoError]);

//     return (
//         <div className="short-videos-container">
//             <div className="short-videos-grid">
//                 <div className="grid-header">
//                     <h1 className="grid-title">Make a Difference</h1>
//                     <p className="grid-subtitle">Support these amazing causes and help change lives around the world</p>
//                 </div>

//                 {videos.map((video) => (
//                     <div
//                         key={video.id}
//                         className="video-card"
//                         onClick={() => handleVideoClick(video.id)}
//                     >
//                         {video.badge === "urgent" && (
//                             <div className="urgent-badge">Urgent Need</div>
//                         )}
//                         {video.badge === "verified" && (
//                             <div className="verified-badge">Verified</div>
//                         )}

//                         <div className="video-progress">
//                             <div
//                                 className="progress-bar"
//                                 style={{ width: `${progressStates[video.id] || 0}%` }}
//                             ></div>
//                         </div>

//                         <div className="video-header">
//                             <h3 className="video-title">{video.title}</h3>
//                             <div className="video-controls">
//                                 <button
//                                     className="control-btn"
//                                     onClick={(e) => handlePlayPause(video.id, e)}
//                                 >
//                                     {playingStates[video.id] ? 'Pause' : 'Play'}
//                                 </button>
//                             </div>
//                         </div>

//                         <div className="video-wrapper">
//                             {loadingStates[video.id] && (
//                                 <div className="video-loading">
//                                     <div className="loading-spinner"></div>
//                                     Loading...
//                                 </div>
//                             )}

//                             {errorStates[video.id] && (
//                                 <div className="video-error">
//                                     <p>Failed to load video</p>
//                                     <button
//                                         className="retry-btn"
//                                         onClick={(e) => retryVideo(video.id, e)}
//                                     >
//                                         Retry
//                                     </button>
//                                 </div>
//                             )}

//                             {!errorStates[video.id] && (
//                                 <video
//                                     ref={el => videoRefs.current[video.id] = el}
//                                     className="video-player"
//                                     muted
//                                     playsInline
//                                     preload="metadata"
//                                     loop
//                                 >
//                                     <source src={video.url} type="video/mp4" />
//                                     Your browser does not support the video tag.
//                                 </video>
//                             )}

//                             <div className="play-pause-overlay">
//                                 <div className="play-pause-icon">
//                                     {playingStates[video.id] ? '❚❚' : '▶'}
//                                 </div>
//                             </div>
//                         </div>

//                         <div className="video-footer">
//                             {/* <div className="video-stats">
//                                 <div className="stat">
//                                     <span>👁️ {video.views}</span>
//                                 </div>
//                                 <div className="stat">
//                                     <span>🎯 {video.impact}</span>
//                                 </div>
//                             </div> */}

//                             <div className="charity-info">
//                                 <h3 className="charity-name">{video.charityName}</h3>
//                                 <p className="charity-description">{video.description}</p>
//                             </div>

//                             <a href="https://pmny.in/HJ2AWtxjLqtp" target="" rel="noopener noreferrer">
//                                 <button className="donation-cta">
//                                     Donate
//                                 </button>
//                             </a>
//                         </div>
//                     </div>
//                 ))}
//             </div>
//         </div>
//     );
// };

// export default ActivityVideos;